import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckCircleIcon, StarIcon, UsersIcon, TrendingUpIcon } from 'lucide-react';
const trustBadges = [{
  icon: UsersIcon,
  text: '110+ Entrepreneurs'
}, {
  icon: StarIcon,
  text: 'Close CRM Partner'
}, {
  icon: TrendingUpIcon,
  text: '3.2x ROI Average'
}];
const clientLogos = ['Alessandro Principe', 'Ennea Scholars', 'automadeit', 'Ecom Wave', 'Thorsten Wittmann'];
// Helper function to get current month name in English
const getCurrentMonthName = () => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return months[new Date().getMonth()];
};
// Helper function to get available spots based on day of month
const getAvailableSpots = () => {
  const dayOfMonth = new Date().getDate();
  if (dayOfMonth <= 10) return 3;
  if (dayOfMonth <= 20) return 2;
  return 1;
};
export function Hero() {
  const currentMonth = getCurrentMonthName();
  const availableSpots = getAvailableSpots();
  return <section className="relative min-h-screen bg-[#0a0a0a] overflow-hidden" data-id="element-102" data-mp="3o59c3jgch6ej">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#0a0a0a] to-[#0c1929] pointer-events-none" data-id="element-103" data-mp="3e9fuvmrokoqf" />
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-[#0ea5e9]/5 blur-[120px] pointer-events-none" data-id="element-104" data-mp="30hrgfctylo6a" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 lg:pt-32 lg:pb-24" data-id="element-105" data-mp="359t0vm1a6l7e">
        {/* Badge */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="flex justify-center mb-8" data-id="element-106" data-mp="2qlepuntpnqfr">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0ea5e9]/10 border border-[#0ea5e9]/20 text-[#0ea5e9] text-sm font-medium" data-id="element-107" data-mp="2lk8usjckvr32">
            <span className="w-2 h-2 bg-[#0ea5e9] rounded-full animate-pulse" data-id="element-108" data-mp="nat6xa8uyik6" />
            Only {availableSpots} {availableSpots === 1 ? 'spot' : 'spots'} left
            in {currentMonth}
          </span>
        </motion.div>

        {/* Main headline */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="text-center max-w-5xl mx-auto" data-id="element-109" data-mp="db2dzczy4bm7">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight tracking-tight" data-id="element-110" data-mp="x1efnqi8x3gt">
            The <span className="text-[#0ea5e9]" data-id="element-111" data-mp="3lt73mli5bhrm">Close CRM Agency</span> for{' '}
            <span className="relative" data-id="element-112" data-mp="3hrllakmueba1">
              scalable sales
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none" data-id="element-113" data-mp="3htg9q8xboliv">
                <path d="M2 10C50 4 150 2 298 10" stroke="#0ea5e9" strokeWidth="3" strokeLinecap="round" data-id="element-114" data-mp="21bpmjgknzt1q" />
              </svg>
            </span>
          </h1>

          <p className="mt-8 text-lg sm:text-xl lg:text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed" data-id="element-115" data-mp="2b2k6geafugk0">
            We implement Close CRM so your sales team closes{' '}
            <span className="text-white font-medium" data-id="element-116" data-mp="2kx2hk4i6pedu">47% more deals</span> –
            without extra staff, without chaos, without data loss.
          </p>
        </motion.div>

        {/* Trust badges */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="flex flex-wrap justify-center gap-6 mt-10" data-id="element-117" data-mp="2txhdrywosdw4">
          {trustBadges.map((badge, index) => <div key={index} className="flex items-center gap-2 text-gray-400" data-id="element-118" data-mp="3vh5palvg3yel" data-mpi={index}>
              <badge.icon className="w-5 h-5 text-[#0ea5e9]" data-id="element-119" data-mp="e88vpzudsc3f" />
              <span className="text-sm font-medium" data-id="element-120" data-mp="9aqku3wszmss">{badge.text}</span>
            </div>)}
        </motion.div>

        {/* CTAs */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="flex flex-col sm:flex-row justify-center gap-4 mt-12" data-id="element-121" data-mp="3hr22ntsnm2om">
          <a href="/free-strategy-call" className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0ea5e9] text-white font-semibold rounded-lg hover:bg-[#0284c7] transition-all duration-200 shadow-lg shadow-[#0ea5e9]/25" data-id="element-122" data-mp="2yzsupoyj7lz5">
            Free Strategy Call
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-123" data-mp="1e93z5ukh2sq7" />
          </a>
          <a href="#case-studies" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/5 text-white font-semibold rounded-lg border border-white/10 hover:bg-white/10 transition-all duration-200" data-id="element-124" data-mp="zkzmb783pbfc">
            View Success Stories
          </a>
        </motion.div>

        {/* Risk reversal */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-gray-500" data-id="element-125" data-mp="1x7gha5td3cxx">
          <span className="flex items-center gap-2" data-id="element-126" data-mp="buwzb5j1mvnl">
            <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-127" data-mp="bvoifgvgf0wz" />
            30-Day Money-Back Guarantee
          </span>
          <span className="flex items-center gap-2" data-id="element-128" data-mp="2ejn4hrvx0cf9">
            <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-129" data-mp="29i7ju0xng7xe" />
            Free CRM Analysis
          </span>
          <span className="flex items-center gap-2" data-id="element-130" data-mp="24qg28fwh1cje">
            <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-131" data-mp="1zsnxumen3xyb" />
            No Hidden Costs
          </span>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.7
      }} className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-20 pt-12 border-t border-white/10" data-id="element-132" data-mp="1vsnld7b3pwtw">
          {[{
          value: '110+',
          label: 'Entrepreneurs Supported'
        }, {
          value: '3.2x',
          label: 'Average ROI'
        }, {
          value: '89%',
          label: 'Time Saved in Sales'
        }, {
          value: '47%',
          label: 'More Closed Deals'
        }].map((stat, index) => <div key={index} className="text-center" data-id="element-133" data-mp="3sj8gbyea0f5p" data-mpi={index}>
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white" data-id="element-134" data-mp="1b41m308jtwv">
                {stat.value}
              </div>
              <div className="mt-2 text-sm text-gray-500" data-id="element-135" data-mp="30rsjzcmx3e2a">{stat.label}</div>
            </div>)}
        </motion.div>

        {/* Client logos */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.9
      }} className="mt-20" data-id="element-136" data-mp="39q4gfvagt62n">
          <p className="text-center text-sm text-gray-600 mb-8" data-id="element-137" data-mp="p2sxftc9n0ik">
            TRUSTED BY LEADING COMPANIES
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-16 mb-16" data-id="element-138" data-mp="1f3ao003r10ou">
            {clientLogos.map((logo, index) => <span key={index} className="text-gray-600 font-semibold text-lg" data-id="element-139" data-mp="1b3a2jvi8fp8a" data-mpi={index}>
                {logo}
              </span>)}
          </div>

          {/* Close CRM Partner Badges */}
          <div className="mt-12 pt-12 border-t border-white/10" data-id="element-140" data-mp="1k1vhlgohwm4b">
            <p className="text-center text-sm text-gray-600 mb-8" data-id="element-141" data-mp="1yr1lvj26x2j2">
              OFFICIAL CLOSE CRM PARTNER
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-12" data-id="element-142" data-mp="1tropo7pfij3t">
              <motion.img initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 1.0
            }} src="https://cdn.magicpatterns.com/uploads/3FWPoSStYkTeUgaax2MNNb/Close_PartnerBadge_Top10_24.png" alt="Close CRM Top 10 Partner 2024" className="w-32 h-32 object-contain" loading="lazy" width="128" height="128" data-id="element-143" data-mp="27r6j50do6kji" />
              <motion.img initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 1.1
            }} src="https://cdn.magicpatterns.com/uploads/pQdCjF5fWpKETcGWQmuMHZ/Close_PartnerBadge_Since22.png" alt="Close CRM Partner Since 2022" className="h-16 object-contain" loading="lazy" width="auto" height="64" data-id="element-144" data-mp="2fbikl4v8irq2" />
              <motion.img initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 1.2
            }} src="https://cdn.magicpatterns.com/uploads/ae4DuFRYPVbUpuQ7e3dsY4/Close_PartnerBadge_Tier3.png" alt="Close CRM Solutions Partner Tier 3" className="w-32 h-32 object-contain" loading="lazy" width="128" height="128" data-id="element-145" data-mp="3qotd9plmuikd" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}